import { create } from 'zustand';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import type { VoiceObservation, VoiceErrorKey } from './voice-contract';
import {
  applyOccupancy,
  applyJoined,
  applyLeft,
  applyChannelPruned,
  type VoiceOccupancyMap,
  type VoiceParticipant,
} from './voice-presence';

/**
 * Last owned webhook-memory observation, refreshed by native GET after private
 * hints and foreground polling/focus/reconnect. Unknown and failed reads remain
 * distinct from an observed empty room. The actual in-room roster for people
 * who ARE connected comes from the LiveKit SDK inside `VoiceRoom`, not here.
 *
 * All mutation logic is the pure reducers in `./voice-presence`; this is just a
 * thin Zustand wrapper (same shape as `presence-store`). Read non-React via
 * `useVoicePresenceStore.getState()`.
 */
interface VoicePresenceState {
  byChannelId: VoiceOccupancyMap;
  observations: Record<string, { phase: 'loading' | 'ready' | 'error'; value?: VoiceObservation; error?: VoiceErrorKey }>;
  reading: (id: string) => void;
  observe: (id: string, value: VoiceObservation) => void;
  failed: (id: string, key: VoiceErrorKey, denied: boolean) => void;
  setOccupancy: (channelId: string, participants: VoiceParticipant[]) => void;
  join: (channelId: string, participant: VoiceParticipant) => void;
  leave: (channelId: string, userId: string) => void;
  /** Drop a channel's key entirely — call when the channel is deleted. */
  prune: (channelId: string) => void;
  /** Wipe everything — call on logout. */
  clear: () => void;
}

export const useVoicePresenceStore = create<VoicePresenceState>((set) => ({
  byChannelId: {}, observations: {},
  reading: id => set(s => ({ observations: { ...s.observations, [id]: { ...s.observations[id], phase: 'loading' } } })),
  observe: (id, value) => set(s => ({ byChannelId: applyOccupancy(s.byChannelId,id,value.participants), observations: { ...s.observations, [id]: {phase:'ready', value} } })),
  failed: (id, key, denied) => set(s => ({ byChannelId: denied ? applyChannelPruned(s.byChannelId,id) : s.byChannelId, observations: { ...s.observations, [id]: {phase:'error', error:key, ...(!denied && s.observations[id]?.value ? {value:s.observations[id].value}: {})} } })),
  setOccupancy: (channelId, participants) =>
    set((s) => ({ byChannelId: applyOccupancy(s.byChannelId, channelId, participants) })),
  join: (channelId, participant) =>
    set((s) => ({ byChannelId: applyJoined(s.byChannelId, channelId, participant) })),
  leave: (channelId, userId) =>
    set((s) => ({ byChannelId: applyLeft(s.byChannelId, channelId, userId) })),
  prune: (channelId) =>
    set((s) => { const observations={...s.observations}; delete observations[channelId]; return { byChannelId: applyChannelPruned(s.byChannelId, channelId), observations }; }),
  clear: () => set({ byChannelId: {}, observations: {} }),
}));

/**
 * Selector hook — the live occupancy count for ONE voice channel. Components
 * only re-render when that channel's count changes.
 */
export function useVoiceOccupancy(channelId: string | undefined): number {
  return useVoicePresenceStore((s) =>
    channelId ? s.byChannelId[channelId]?.length ?? 0 : 0,
  );
}

// Synchronous account/workspace retirement, including batched A→B→A.
useAuthStore.subscribe((next,previous)=>{if(sessionScope(next)!==sessionScope(previous))useVoicePresenceStore.getState().clear();});
export function useVoiceObservation(channel: string) { return useVoicePresenceStore(s=>s.observations[channel]); }
